/**
 * UI language: i18next; translation files are locales/<language>/web.json (UI) and server.json (messages from the backend) at the repo root;
 * adding a language means adding a folder (see locales/README.md); it's picked up automatically here, and the dropdown in Settings gets it too.
 * Usage: call t with the original Chinese sentence as the first argument and variables as the second; the key is the Chinese source text, variables written as {name}; rules in packages/shared/src/i18n.ts.
 * The language setting follows the backend's settings.language (synced across devices); before login, use the one remembered on this device.
 */
import { useSyncExternalStore } from 'react';
import i18next from 'i18next';
import { fallbackLanguages, MessageMatcher, resolveLanguage, SOURCE_LANGUAGE, type Language, type LanguagePref, type Vars } from '@momomail/shared';

type Json = Record<string, string>;
const files = import.meta.glob<Json>('../../../../locales/*/{language,web,server}.json', { eager: true, import: 'default' });

const names: Record<Language, string> = {};
const web: Record<Language, Json> = {};
const server: Record<Language, Json> = {};
for (const [file, data] of Object.entries(files)) {
  const [, lang, kind] = /locales\/([^/]+)\/(\w+)\.json$/.exec(file)!;
  if (kind === 'language') names[lang!] = (data as unknown as { name: string }).name;
  else (kind === 'web' ? web : server)[lang!] = data;
}

/** Available languages (Chinese is the source language, always present), in the order of the settings dropdown. */
export const LANGUAGES: { code: Language; name: string }[] = [SOURCE_LANGUAGE, ...Object.keys(names).filter((c) => c !== SOURCE_LANGUAGE).sort()].map((code) => ({
  code,
  name: names[code] ?? code,
}));
const available = LANGUAGES.map((l) => l.code);

const KEY = 'momomail.language';
const listeners = new Set<() => void>();
const systemLocales = () => (navigator.languages?.length ? navigator.languages : [navigator.language]);

function stored(): LanguagePref {
  try {
    return localStorage.getItem(KEY) || 'system';
  } catch {
    return 'system';
  }
}

let pref: LanguagePref = stored();
let current: Language = resolveLanguage(pref, systemLocales(), available);

void i18next.init({
  lng: current,
  resources: Object.fromEntries(available.map((code) => [code, { translation: { ...server[code], ...web[code] } }])),
  fallbackLng: (code: string) => fallbackLanguages(code),
  // Keys are Chinese source sentences: the . and : inside them are not nesting / namespaces
  keySeparator: false,
  nsSeparator: false,
  interpolation: { prefix: '{', suffix: '}', escapeValue: false },
  returnEmptyString: false,
  initAsync: false,
});

let matcher = new MessageMatcher({});
function refresh() {
  const chain = [current, ...fallbackLanguages(current)].reverse();
  matcher = new MessageMatcher(Object.assign({}, ...chain.map((c) => server[c] ?? {})));
  document.documentElement.lang = current === 'zh' ? 'zh-CN' : current;
}
refresh();

/** The language currently in use. */
export function language(): Language {
  return current;
}

/** Locale used for date and number formatting. */
export function locale(): string {
  return current === 'zh' ? 'zh-CN' : current === 'en' ? 'en-US' : current;
}

/** A UI sentence. Untranslated ones show the Chinese source text (variables still filled in). */
export function t(zh: string, vars?: Vars): string {
  return i18next.t(zh, { ...vars, defaultValue: zh });
}

/** Chinese messages from the backend or elsewhere: translate if server.json has it (including whole sentences with variables). */
export function tMessage(text: string): string {
  return current === SOURCE_LANGUAGE ? text : matcher.translate(text);
}

/** The language in settings ("跟随系统" (Follow system) or a specific language). On change, redraw the whole UI and tell the desktop shell (tray menu, system notifications). */
export function setLanguagePref(next: LanguagePref | undefined): void {
  pref = next || 'system';
  try {
    localStorage.setItem(KEY, pref);
  } catch {}
  const lang = resolveLanguage(pref, systemLocales(), available);
  window.momomailShell?.prefs.set({ language: lang }).catch(() => {});
  if (lang === current) return;
  current = lang;
  void i18next.changeLanguage(lang);
  refresh();
  for (const l of listeners) l();
}

export function languagePref(): LanguagePref {
  return pref;
}

/** For components: redraw when the language changes. The outermost component (Root in main.tsx) uses it, so the whole tree redraws. */
export function useLanguage(): Language {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
}
